import React, { useEffect, useState } from 'react';
import {
  Elements,
  useStripe,
  useElements,
  PaymentElement,
} from '@stripe/react-stripe-js';
import { loadStripe, StripeElementsOptions } from '@stripe/stripe-js';
import { apiClient } from '../config/ApiClient';
import { showError, showSuccess } from './Alert';
import {
  Button,
  Col,
  Container,
  Form,
  Row,
  Spinner,
  Card,
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';

const stripePromise = loadStripe(
  'pk_test_51OisEFJp3TLgm0DXNLyqxxi81S3zvnpklFhiZAbe3HlVQAKQJp3p8BbTF5PvtkLzintB07wcMgbqngUyAzts5bQe00XbBVcCnn'
);

const CheckoutFormInner: React.FC<{
  orderId: string;
  amount: number;
  currency: string;
}> = ({ orderId, amount, currency }) => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();

  const [processing, setProcessing] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setProcessing(true);
    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: window.location.origin + '/checkout/success',
      },
      redirect: 'if_required',
    });

    if (error) {
      showError(error.message || 'Payment failed');
      setProcessing(false);
      return;
    }

    try {
      await apiClient.post('/orders/place');
      showSuccess('Payment successful and order placed!');
      navigate('/checkout/success');
    } catch (err: any) {
      showError(err.response?.data?.message || 'Failed to place order');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Form onSubmit={handleSubmit} className="checkout-form-section">
      <h5 className="mt-4">PAYMENT METHOD</h5>
      <PaymentElement />
      <div className="d-grid mt-4">
        <Button type="submit" variant="dark" disabled={processing || !stripe}>
          {processing ? (
            <Spinner size="sm" animation="border" />
          ) : (
            'PLACE ORDER'
          )}
        </Button>
      </div>
    </Form>
  );
};

const CheckoutForm: React.FC = () => {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [orderId, setOrderId] = useState<string | null>(null);
  const [amount, setAmount] = useState<number | null>(null);
  const [currency, setCurrency] = useState<string>('CAD');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPaymentIntent = async () => {
      try {
        const res = await apiClient.post('/orders/checkout');
        setClientSecret(res.data.clientSecret);
        setOrderId(res.data.orderId);
        setAmount(res.data.totalAmount);
        setCurrency(res.data.currency || 'CAD');
      } catch (err: any) {
        showError(err.response?.data?.message || 'Failed to initiate payment');
      } finally {
        setLoading(false);
      }
    };

    fetchPaymentIntent();
  }, []);

  if (loading)
    return (
      <div className="text-center my-5">
        <Spinner animation="border" />
      </div>
    );

  if (!clientSecret || !orderId || amount === null)
    return (
      <div className="text-center my-5">Unable to proceed with checkout.</div>
    );

  return (
    <Container className="checkout-page my-5">
      <Row>
        <Col md={8}>
          <Card className="p-4 mb-4">
            <h4>CHECKOUT</h4>
            <Form>
              <h5 className="mt-3">SHIPPING ADDRESS</h5>
              <Row>
                <Col>
                  <Form.Group className="mb-2">
                    <Form.Label>First Name</Form.Label>
                    <Form.Control placeholder="John" />
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group className="mb-2">
                    <Form.Label>Last Name</Form.Label>
                    <Form.Control placeholder="Doe" />
                  </Form.Group>
                </Col>
              </Row>
              <Form.Group className="mb-2">
                <Form.Label>Address</Form.Label>
                <Form.Control placeholder="123 Main Street" />
              </Form.Group>
              <Row>
                <Col>
                  <Form.Group className="mb-2">
                    <Form.Label>City</Form.Label>
                    <Form.Control />
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group className="mb-2">
                    <Form.Label>Postal Code</Form.Label>
                    <Form.Control />
                  </Form.Group>
                </Col>
              </Row>
              <h5 className="mt-4">SHIPPING METHOD</h5>
              <Form.Check
                type="radio"
                name="shipping"
                label="Standard Shipping"
                defaultChecked
              />
              <Form.Check
                type="radio"
                name="shipping"
                label="Express Shipping"
              />
            </Form>
          </Card>

          <Elements
            stripe={stripePromise}
            options={
              {
                clientSecret: clientSecret!,
                appearance: {
                  theme: 'stripe',
                  labels: 'floating',
                  variables: {
                    colorPrimary: '#000',
                    colorText: '#1b1b1b',
                    fontFamily: 'Questrial, sans-serif',
                  },
                },
                layout: 'tabs',
              } as StripeElementsOptions
            }
          >
            <CheckoutFormInner
              orderId={orderId}
              amount={amount}
              currency={currency}
            />
          </Elements>
        </Col>

        <Col md={4}>
          <Card className="p-4">
            <h5>ORDER SUMMARY</h5>
            <hr />
            <div className="d-flex justify-content-between">
              <span>Subtotal</span>
              <span>
                {currency} {amount.toFixed(2)}
              </span>
            </div>
            <div className="d-flex justify-content-between">
              <span>Shipping</span>
              <span>Calculated at checkout</span>
            </div>
            <hr />
            <div className="d-flex justify-content-between fw-bold">
              <span>Total</span>
              <span>
                {currency} {amount.toFixed(2)}
              </span>
            </div>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default CheckoutForm;
